/**
 * Client Certificate Hooks
 * ISO 27001 A.9.4.2 - Secure authentication mechanisms (mTLS)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  clientCertificateApi,
  CertificateUploadInput,
  CertificateUpdateInput,
} from '../api/client-certificate.api';

// Query keys
const CERTIFICATES_KEY = ['client-certificates'];
const CERTIFICATE_STATS_KEY = ['client-certificates', 'stats'];
const CERTIFICATES_EXPIRING_KEY = ['client-certificates', 'expiring'];

/**
 * Hook to get all certificates
 */
export function useCertificates(options?: {
  isActive?: boolean;
  includeExpired?: boolean;
}) {
  return useQuery({
    queryKey: [...CERTIFICATES_KEY, options],
    queryFn: () => clientCertificateApi.getCertificates(options),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to get a specific certificate
 */
export function useCertificate(id: string) {
  return useQuery({
    queryKey: [...CERTIFICATES_KEY, id],
    queryFn: () => clientCertificateApi.getCertificate(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to get certificate statistics
 */
export function useCertificateStats() {
  return useQuery({
    queryKey: CERTIFICATE_STATS_KEY,
    queryFn: () => clientCertificateApi.getStats(),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to get certificates expiring soon
 */
export function useCertificatesExpiringSoon(days?: number) {
  return useQuery({
    queryKey: [...CERTIFICATES_EXPIRING_KEY, days],
    queryFn: () => clientCertificateApi.getExpiringSoon(days),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to get certificate usage logs
 */
export function useCertificateUsageLogs(
  id: string,
  options?: {
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
  }
) {
  return useQuery({
    queryKey: [...CERTIFICATES_KEY, id, 'logs', options],
    queryFn: () => clientCertificateApi.getUsageLogs(id, options),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to upload a new certificate
 */
export function useUploadCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CertificateUploadInput) =>
      clientCertificateApi.uploadCertificate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CERTIFICATES_KEY });
      queryClient.invalidateQueries({ queryKey: CERTIFICATE_STATS_KEY });
    },
  });
}

/**
 * Hook to update certificate settings
 */
export function useUpdateCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CertificateUpdateInput }) =>
      clientCertificateApi.updateCertificate(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: CERTIFICATES_KEY });
      queryClient.invalidateQueries({ queryKey: [...CERTIFICATES_KEY, id] });
    },
  });
}

/**
 * Hook to revoke a certificate
 */
export function useRevokeCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      clientCertificateApi.revokeCertificate(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CERTIFICATES_KEY });
      queryClient.invalidateQueries({ queryKey: CERTIFICATE_STATS_KEY });
    },
  });
}

/**
 * Hook to delete a certificate
 */
export function useDeleteCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => clientCertificateApi.deleteCertificate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CERTIFICATES_KEY });
      queryClient.invalidateQueries({ queryKey: CERTIFICATE_STATS_KEY });
    },
  });
}
